/*  ========================================================================
    # Components - Image Slider - Single Slide
    ========================================================================  */

import { motion, useIsPresent, useMotionValue, useReducedMotion } from 'motion/react';
import PropTypes from 'prop-types';
import React from 'react';
import useTranslation from '../../hooks/useTranslation';

const slideVariants = {
  out: direction => ({
    x: `${direction * -1 * 100}%`
  }),
  in: direction => ({
    x: `${direction * 100}%`
  }),
  current: {
    x: 0
  }
};

const captionVariants = {
  out: direction => ({
    x: `${direction * -1 * 20}%`,
    opacity: 0
  }),
  in: direction => ({
    x: `${direction * 20}%`,
    opacity: 0
  }),
  current: {
    x: 0,
    opacity: 1
  }
};

const variantsReduced = {
  out: {
    opacity: 0
  },
  in: {
    opacity: 0
  },
  current: {
    opacity: 1
  }
};

const SingleSlide = ({
  index,
  count,
  direction,
  image,
  imageFit = 'cover',
  inView,
  width,
  canGoBack,
  canGoNext,
  caption,
  handleBack,
  handleNext
}) => {
  const dragX = useMotionValue(0);
  const isPresent = useIsPresent();
  const __ = useTranslation();
  const reduced = useReducedMotion();

  return (
    <motion.div
      className='c-image-slider__slide'
      role='group'
      aria-hidden={!isPresent}
      aria-roledescription='slide'
      aria-label={__('images-slider-slide')
        .replace('%s', index + 1)
        .replace('%s', count)}
      variants={reduced ? variantsReduced : slideVariants}
      initial='in'
      animate='current'
      exit='out'
      custom={direction}
      transition={{
        visualDuration: 0.6,
        bounce: 0.1,
        type: 'spring'
      }}
    >
      <motion.img
        className='c-image-slider__image'
        srcSet={inView ? image.srcSet : ''}
        sizes={`${width}px`}
        src={inView ? image.sizes.medium : image.sizes.placeholder}
        alt={image.alt}
        // Drag enabling
        drag='x'
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={{ left: canGoNext ? 0.25 : 0.02, right: canGoBack ? 0.25 : 0.02 }}
        style={{ x: dragX, objectFit: imageFit }}
        onDragEnd={() => {
          const x = dragX.get();
          if (x <= -50 && canGoNext) handleNext();
          if (x >= 50 && canGoBack) handleBack();
        }}
      />
      {caption && image.caption && (
        <motion.div
          custom={direction}
          className={`c-image-slider__caption-box c-image-slider__caption-box--${imageFit}`}
          variants={reduced ? variantsReduced : captionVariants}
          transition={{ type: 'tween', duration: 1, ease: 'easeOut' }}
        >
          <p className='c-image-slider__caption'>{image.caption}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

SingleSlide.propTypes = {
  image: PropTypes.shape({
    ID: PropTypes.number,
    alt: PropTypes.string,
    sizes: PropTypes.object, // No more detail here
    srcSet: PropTypes.string,
    url: PropTypes.string,
    title: PropTypes.string,
    caption: PropTypes.string,
    description: PropTypes.string
  }),
  caption: PropTypes.bool,
  index: PropTypes.number,
  width: PropTypes.number,
  count: PropTypes.number,
  direction: PropTypes.number,
  imageFit: PropTypes.oneOf(['contain', 'cover']),
  inView: PropTypes.bool,
  canGoBack: PropTypes.bool,
  canGoNext: PropTypes.bool,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func
};

export default SingleSlide;
